import { UploadStatus } from "@/shared/enums/upload-status";
import { StreamingDto } from "@/shared/services/streamingService";
import { t } from "i18next";

const COMPLETED_STATUSES = ["COMPLETED", "FINISHED", "SUCCESS", "FIN"];
const ERROR_STATUSES = ["ERROR", "FAILED", "CANCELLED", "ERR"];

export interface StreamingStatusResult {
	isCompleted: boolean;
	isFailed: boolean;
	statusCode: string;
	errorMessage?: string;
	finalStatus: UploadStatus;
}

export const determineStreamingStatus = (
	streamingData: StreamingDto,
): StreamingStatusResult => {
	const statusCode: string = streamingData.status.code;

	const isCompleted = COMPLETED_STATUSES.includes(statusCode?.toUpperCase());

	const isFailed = Boolean(
		ERROR_STATUSES.includes(statusCode?.toUpperCase()) ||
			(statusCode && /^E[A-Z0-9]+$/i.test(statusCode)) ||
			(streamingData.executionErrors &&
				streamingData.executionErrors.length > 0),
	);

	const errorMessage = isFailed
		? extractErrorMessage(streamingData, statusCode)
		: undefined;

	const finalStatus = isCompleted
		? UploadStatus.COMPLETED
		: UploadStatus.ANALYZING_ERROR;

	return {
		isCompleted,
		isFailed,
		statusCode,
		errorMessage,
		finalStatus,
	};
};

const extractErrorMessage = (
	streamingData: StreamingDto,
	statusCode: string,
): string => {
	const statusDescription = streamingData.status.description;

	const lastError =
		streamingData.executionErrors && streamingData.executionErrors.length > 0
			? streamingData.executionErrors[streamingData.executionErrors.length - 1]
			: undefined;

	return (
		statusDescription ||
		lastError?.statusMessage ||
		`${t("artifact.status.error-code")}: ${statusCode}`
	);
};

export const dispatchProfilingEvent = (
	eventType: string,
	detail: {
		fileName?: string;
		artifactId?: string;
		success?: boolean;
		errorMessage?: string;
	},
): void => {
	const event = new CustomEvent(eventType, { detail });
	document.dispatchEvent(event);
};

export const updateCellToProfiledState = (
	cell: HTMLElement,
	artifactId: string,
	success = true,
	errorMessage?: string,
): void => {
	const statusContainer = cell.querySelector(".status-icon-container");
	if (statusContainer) {
		if (success) {
			statusContainer.innerHTML = `
        <span class='status-icon mdi mdi-check-circle-outline'></span>
        <span>${t("artifact.status.profiled")}</span>
      `;
		} else {
			statusContainer.innerHTML = `
        <span class='status-icon mdi mdi-alert-circle-outline text-danger'></span>
        <span class='text-danger'>${t("artifact.status.profiling-failed")}</span>
      `;

			if (errorMessage) {
				statusContainer.setAttribute("title", errorMessage);
			}
		}
	}

	cell.classList.remove("profiling-status-cell");

	const row = cell.closest("tr");
	if (row) {
		const deleteButton = row
			.querySelector(".mdi-trash-can")
			?.closest("a") as HTMLElement;
		if (deleteButton) {
			deleteButton.style.cursor = "pointer";
			deleteButton.style.opacity = "1";
			deleteButton.title = t("delete");

			dispatchProfilingEvent("artifactProfilingCompleted", { artifactId });
		}
	}
};
